import { cva, cx } from 'cva'
import type { FC, ReactNode } from 'react'
import type { IconType, Maybe } from '../../frontend-types'
import { FontAwesomeIcon } from './FontAwesomeIcon'

export type TimelinePhaseType = {
	id: Maybe<string>
	status: 'current' | 'completed' | 'future'
	icon: IconType
	content: ReactNode
}

type TimelinePhaseProps = {
	timelinePhase: TimelinePhaseType
	isLast?: boolean
}

export const squareIconVariants = cva({
	base: 'w-icon-sm border-(length:--stroke-md) border-blue rounded-sm aspect-square flex items-center justify-center',
	variants: {
		status: {
			current: 'bg-green-20 text-blue',
			completed: 'bg-blue text-white',
			future: 'text-blue bg-blue-tint-10',
		},
	},
})

export const TimelinePhase: FC<TimelinePhaseProps> = ({ timelinePhase, isLast }) => {
	const { status, icon, content } = timelinePhase

	const stateIcon =
		status === 'current' ? 'location-dot' : status === 'completed' ? 'circle-check' : null

	return (
		<div className="flex flex-row gap-x-2xl tablet-l:flex-col">
			<div className="flex flex-col tablet-l:flex-row">
				<div className="flex w-fit flex-col items-start gap-sm">
					{stateIcon ? (
						<FontAwesomeIcon
							icon={['far', stateIcon]}
							size="2xl"
							className="self-center text-blue"
						/>
					) : (
						<div className="hidden h-8 w-8 tablet-l:block" />
					)}
					<div className="flex items-center justify-center">
						<div className={squareIconVariants({ status })}>
							<FontAwesomeIcon icon={icon} size="3xl" />
						</div>
					</div>
				</div>
				<div
					className={cx(
						isLast && 'hidden',
						'mt-2 mb-2 w-0.5 flex-1 self-center border-l-[2px] scale-x-75 border-blue',
						'tablet-l:mt-0 tablet-l:mb-8 tablet-l:h-0.5 tablet-l:self-end tablet-l:border-t-[2px] tablet-l:border-l-0 tablet-l:scale-x-100 tablet-l:scale-y-75',
						status === 'completed' ? 'border-solid' : 'border-dashed',
					)}
				/>
			</div>
			<div
				className={cx(
					status !== 'future' ? 'mt-10' : '',
					'flex flex-col gap-y-sm pr-xl tablet-l:mt-2xl tablet-l:mr-xl [&_*]:mt-0! [&_p]:mt-xs! [&_:is(h3,h4,h5,h6)]:mt-sm! [&_.cms-blocks>.cms-block:first-child_*]:mt-0! [&_h3]:text-blue',
					!isLast && 'pb-5xl tablet-l:pb-0 min-h-38 tablet-l:min-h-0',
				)}
			>
				{content}
			</div>
		</div>
	)
}
